import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Paper,
  Stack,
  Avatar,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  SmartToy as BotIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Assignment as SummaryIcon,
} from '@mui/icons-material';
import { HandoffField } from '../../services/handoffApi';
import { 
  WHATSAPP_BG_COLOR, 
  WHATSAPP_BG_IMAGE_URL, 
  WHATSAPP_MESSAGE_USER_BG, 
  WHATSAPP_MESSAGE_BOT_BG 
} from '../../constants/whatsapp';


interface Message {
  id: string;
  text: string;
  sender: 'bot' | 'user' | 'system';
  timestamp: Date;
}

interface ChatSimulationProps {
  fields: HandoffField[];
  isOpen: boolean;
  onClose: () => void;
  initialView?: 'chat' | 'summary';
}

const generateDummyResponse = (field: HandoffField) => {
  
  return field.example || 'Sample response';
};

const ChatSimulation: React.FC<ChatSimulationProps> = ({ fields, isOpen, onClose, initialView = 'chat' }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isSimulating, setIsSimulating] = useState(false);
  const [responses, setResponses] = useState<Record<string, string>>(() => {
    if (initialView === 'summary') {
      const dummyResponses: Record<string, string> = {};
      fields.forEach(f => {
        dummyResponses[f.label] = generateDummyResponse(f);
      });
      return dummyResponses;
    }
    return {};
  });
  const [showSummaryOnMobile, setShowSummaryOnMobile] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const summaryEndRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(true);
  const timeoutIds = useRef<NodeJS.Timeout[]>([]);

  const theme = useTheme();
  // Standardized breakpoint: < 900px (Mobile/Tablet) is Standalone, >= 900px (Desktop) is Side-by-side
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      stopSimulation();
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    summaryEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const stopSimulation = () => {
    timeoutIds.current.forEach(id => clearTimeout(id));
    timeoutIds.current = [];
    if (isMounted.current) {
      setIsSimulating(false);
    }
  };

  const addTimeout = (callback: () => void, delay: number) => {
    const id = setTimeout(() => {
      if (isMounted.current) {
        callback();
      }
    }, delay);
    timeoutIds.current.push(id);
    return id;
  };

  useEffect(() => {
    if (isOpen) {
      if (initialView === 'summary') {
        const dummyResponses: Record<string, string> = {};
        fields.forEach(f => {
          dummyResponses[f.label] = generateDummyResponse(f);
        });
        setResponses(dummyResponses);
        setMessages([]);
        setIsSimulating(false);
        if (isMobile) setShowSummaryOnMobile(true);
      } else {
        setResponses({});
        startSimulation();
      }
    } else {
      stopSimulation();
    }
  }, [isOpen, initialView]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, responses]);

  const handleRegenerate = () => {
    stopSimulation();
    setResponses({});
    startSimulation();
  };

  const startSimulation = () => {
    setMessages([
      {
        id: 'start',
        text: 'To assist you better, please provide some quick details.',
        sender: 'bot',
        timestamp: new Date(),
      },
    ]);
    setResponses({});
    setIsSimulating(true);
    setShowSummaryOnMobile(false);

    // Use safety delay
    addTimeout(() => {
      processNextStep(0, {});
    }, 1000);
  };

  const processNextStep = (step: number, currentResponses: Record<string, string>) => {
    if (!isMounted.current) return;

    if (step >= fields.length) {
      setIsSimulating(false);
      return;
    }

    const field = fields[step];
    const botMessage: Message = {
      id: `bot-${field.id}`,
      text: field.label,
      sender: 'bot',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, botMessage]);

    // Simulate user "typing" delay then response
    addTimeout(() => {
      const responseValue = generateDummyResponse(field);
      const userMessage: Message = {
        id: `user-${field.id}`,
        text: responseValue,
        sender: 'user',
        timestamp: new Date(),
      };

      const updatedResponses = { ...currentResponses, [field.label]: responseValue };

      setMessages((prev) => [...prev, userMessage]);
      setResponses(updatedResponses);

      // Move to next step after another delay
      addTimeout(() => {
        processNextStep(step + 1, updatedResponses);
      }, 1500);
    }, 1500);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          height: isMobile ? '100%' : '80vh',
          maxHeight: isMobile ? '100%' : '85vh',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {initialView === 'chat' && <BotIcon color="primary" />}
          <Typography variant="h6" fontWeight="bold">
            {initialView === 'summary' ? 'View Responses' : 'Chat Demo'}
          </Typography>
        </Box>
        <Box>
          {isMobile && initialView === 'chat' && !showSummaryOnMobile && (
            <Tooltip title="View Summary">
              <IconButton onClick={() => setShowSummaryOnMobile(true)} size="medium">
                <SummaryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {initialView === 'chat' && (
            <Tooltip title="Regenerate Simulation">
              <IconButton onClick={handleRegenerate} disabled={isSimulating} size={isMobile ? "medium" : "small"}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size={isMobile ? "medium" : "small"}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* md breakpoint (900px) splits between Standalone (column) and Side-by-side (row) */}
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, overflow: 'hidden' }}>
        {/* Chat Area - Only shown if not in summary-only mode, or if mobile and not showing summary */}
        {initialView === 'chat' && (!isMobile || !showSummaryOnMobile) && (
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: WHATSAPP_BG_COLOR, // Authentic WhatsApp beige
            backgroundImage: `url("${WHATSAPP_BG_IMAGE_URL}")`, // Classic WhatsApp wallpaper pattern
            backgroundSize: '200px',
            borderRight: { md: '1px solid' },
            borderColor: 'divider',
            height: '100%',
            overflow: 'hidden'
          }}>
            <Box sx={{ flex: 1, overflowY: 'auto', p: isMobile ? 2 : 3 }}>
              <Stack spacing={isMobile ? 1.5 : 2}>
                {messages.map((msg) => (
                  <Box
                    key={msg.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: '90%' }}>
                      {msg.sender === 'bot' && (
                        <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28 }}>
                          <BotIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                      )}
                      <Paper
                        elevation={1}
                        sx={{
                          p: 1.5,
                          borderRadius: msg.sender === 'user' ? '12px 0 12px 12px' : '0 12px 12px 12px',
                          bgcolor: msg.sender === 'user' ? WHATSAPP_MESSAGE_USER_BG : WHATSAPP_MESSAGE_BOT_BG, // User sent = Green, Bot received = White
                          color: 'text.primary',
                          position: 'relative',
                          boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
                          '&::after': isMobile ? {} : {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            [msg.sender === 'user' ? 'right' : 'left']: -8,
                            border: '8px solid transparent',
                            borderTopColor: msg.sender === 'user' ? '#dcf8c6' : '#ffffff',
                          }
                        }}
                      >
                        <Typography variant={isMobile ? "body2" : "body1"}>{msg.text}</Typography>
                      </Paper>
                      {msg.sender === 'user' && (
                        <Avatar sx={{ bgcolor: 'secondary.main', width: 28, height: 28 }}>
                          <PersonIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: msg.sender === 'bot' ? 4.5 : 4.5 }}>
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                ))}
                <div ref={messagesEndRef} />
              </Stack>
            </Box>
            <Box sx={{ p: isMobile ? 1.5 : 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                {isSimulating ? 'Chatbot is asking questions...' : 'Simulation complete'}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Summary Area - Shown if in summary-only mode, or if not on mobile, or if mobile and showing summary */}
        {(initialView === 'summary' || !isMobile || showSummaryOnMobile) && (
          <Box sx={{
            width: { xs: '100%', md: initialView === 'summary' ? '100%' : 350 },
            flex: (initialView === 'summary' || isMobile) ? 1 : 'none',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'white',
            borderTop: { xs: 'none', md: 'none' },
            borderColor: 'grey.100',
            height: '100%'
          }}>
            {initialView === 'chat' && (
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SummaryIcon color="action" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight="bold">View Responses</Typography>
                </Box>
                {isMobile && (
                  <Button size="small" onClick={() => setShowSummaryOnMobile(false)}>
                    Back to Chat
                  </Button>
                )}
              </Box>
            )}
            <Box sx={{ flex: 1, overflowY: 'auto', p: isMobile ? 1.5 : 3 }}>
              {Object.keys(responses).length > 0 ? (
                <Stack spacing={isMobile ? 1 : 2}>
                  <TableContainer component={Paper} variant="outlined" sx={{
                    borderRadius: 1,
                    overflow: 'hidden',
                    borderColor: '#e0e0e0',
                    boxShadow: 'none'
                  }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0', py: 1.5, color: '#5f6368' }}>Questions</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 1.5, color: '#5f6368' }}>Customer Responses</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(responses).map(([label, value]) => (
                          <TableRow key={label} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                            <TableCell sx={{
                              fontSize: '0.85rem',
                              py: 1.5,
                              borderRight: '1px solid #e0e0e0',
                              color: '#3c4043',
                              bgcolor: '#f1f3f4',
                              width: '40%'
                            }}>
                              {label}
                            </TableCell>
                            <TableCell sx={{
                              fontSize: '0.85rem',
                              py: 1.5,
                              color: '#202124',
                              fontWeight: 'medium'
                            }}>
                              {value}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <div ref={summaryEndRef} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center', mt: 2 }}>
                    This is how data will appear once synced to Google Sheets.
                  </Typography>
                  {initialView === 'chat' && !isMobile && (
                    <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, bgcolor: 'primary.50', border: '1px dashed', borderColor: 'primary.main' }}>
                      <Typography variant="body2" color="primary.dark" align="center" sx={{ fontSize: '0.75rem' }}>
                        “These details will be available for your team to contact the customer later.”
                      </Typography>
                    </Box>
                  )}
                </Stack>
              ) : (
                initialView === 'chat' && (
                  <Box sx={{ height: isMobile ? '100px' : '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', p: 2 }}>
                    <SummaryIcon sx={{ fontSize: isMobile ? 32 : 48, mb: 1, opacity: 0.3 }} />
                    <Typography variant="caption" align="center">
                      Summary will appear here as the simulation progresses.
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

    </Dialog>
  );
};


export default ChatSimulation;
